import PropTypes from 'prop-types';
import React from 'react';
import { ButtonGroup } from 'reactstrap';

import {
  ToggleButton,
  ToggleButtonGroup,
  ToggleButtonToolbar,
} from 'src/enosikit/components/Chart/components/ChartControls';

/**
 * Skeleton placeholder for the chart-specific interactive controls within `DashboardControl`,
 * rendered while the dashboard is in an error state. It covers exactly the controls that depend
 * on a successfully fetched dataset:
 * - `ToggleButtonToolbar` + `ToggleButtonGroup` replicate `Chart.ChartControls` (unit toggles:
 *   Wh / $ / CO₂), which renders that same structure internally.
 * - The lower `ButtonGroup` replicates the `chartViewSelector` slot and, when `downloadFunc` is
 *   present, the `FileDownloadSelector` slot.
 *
 * The `DatePickerInTimezone` is deliberately excluded from this skeleton. Because it controls the
 * timespan that initiates data fetching, it must remain fully interactive in the error state so
 * that users can select a different date range and retry the request to recover.
 *
 * The layout mirrors the success branch so the toolbar does not shift or collapse when switching
 * between states.
 *
 * `disabled` is required on every `ToggleButton` for two reasons:
 *   1. Interaction prevention — the buttons are non-functional in the error state;
 *      without `disabled`, clicks would propagate to handlers that expect valid
 *      data and would silently fail or throw.
 *   2. Accessibility — `disabled` removes each button from the tab order
 *      (tabIndex becomes -1) and sets aria-disabled, so keyboard and
 *      assistive-technology users cannot reach or activate skeleton elements
 *      that carry no meaningful action.
 * @param {object} props
 * @param {Function} props.downloadFunc - When non-null, a second placeholder button
 *   is rendered to match the `FileDownloadSelector` present in the success branch.
 * @returns {React.ReactElement} - Skeleton placeholder for `DashboardControl`'s chart controls
 *   during error states
 */
function DashboardControlChartControlsSkeleton({ downloadFunc = null }) {
  return (
    <>
      <ToggleButtonToolbar className="placeholder-glow">
        <ToggleButtonGroup>
          <ToggleButton className="placeholder" style={{ minWidth: '4rem' }} disabled />
          <ToggleButton className="placeholder" style={{ minWidth: '4rem' }} disabled />
          <ToggleButton className="placeholder" style={{ minWidth: '4rem' }} disabled />
        </ToggleButtonGroup>
      </ToggleButtonToolbar>
      <ButtonGroup>
        <div>
          <ToggleButton className="placeholder" style={{ minWidth: '4rem' }} disabled />
          {downloadFunc && (
            <ToggleButton className="placeholder" style={{ minWidth: '4rem' }} disabled />
          )}
        </div>
      </ButtonGroup>
    </>
  );
}

DashboardControlChartControlsSkeleton.propTypes = {
  downloadFunc: PropTypes.func,
};

export default DashboardControlChartControlsSkeleton;
